import { Box, InputAdornment, MenuItem, Popover, Stack, Typography } from "@mui/material";
import IconButton from "../ui/IconButton";
import Input from "../ui/Input";
import LoadingButton from "../ui/LoadingButton";
import Select from "../ui/Select";
import { ChevronLeft, ChevronRight, CalendarMonthOutlined } from "@mui/icons-material";
import { useMemo, useState } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import { useIntl } from "../../contexts/IntlContext";
import { fontWeights } from "../../themes/fontWeights";

type Props = {
  label: string;
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  onToday?: () => void;
  onClear?: () => void;
  /** YYYY-MM-DD; dias anteriores ficam desabilitados. */
  minDate?: string;
  /** YYYY-MM-DD; dias posteriores ficam desabilitados. */
  maxDate?: string;
  minWidth?: number;
  sx?: SxProps<Theme>;
  size?: "small" | "medium";
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  fullWidth?: boolean;
};

const toISODate = (date: Date) => date.toLocaleDateString("sv-SE");

const clampIso = (iso: string, min?: string, max?: string) => {
  if (min && iso < min) return min;
  if (max && iso > max) return max;
  return iso;
};

const clampMonth = (year: number, monthIndex: number) => {
  if (monthIndex < 0) return { year: year - 1, monthIndex: 11 };
  if (monthIndex > 11) return { year: year + 1, monthIndex: 0 };
  return { year, monthIndex };
};

const getDaysInMonth = (year: number, monthIndex: number) =>
  new Date(year, monthIndex + 1, 0).getDate();

const getWeekdayIndex = (date: Date, weekStartsOnSunday: boolean) => {
  const day = date.getDay(); // 0=Sun..6=Sat
  if (weekStartsOnSunday) return day;
  // shift so Monday=0..Sunday=6
  return (day + 6) % 7;
};

export default function DatePickerField({
  label,
  value,
  onChange,
  onToday,
  onClear,
  minDate,
  maxDate,
  minWidth = 200,
  sx,
  size = "small",
  disabled = false,
  error,
  helperText,
  fullWidth,
}: Props) {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuProps = useMemo(
    () => ({
      disablePortal: true,
      disableScrollLock: true,
      PaperProps: { sx: { zIndex: 2000, pointerEvents: "auto" } },
    }),
    []
  );

  const parsedValue = useMemo(() => {
    const date = value ? new Date(`${value}T00:00:00`) : null;
    if (!date || Number.isNaN(date.getTime())) return null;
    return date;
  }, [value]);

  const locale = intl.locale || "pt-BR";
  const weekStartsOnSunday = locale.startsWith("en");

  const initialMonth = parsedValue ?? new Date();
  const [visibleYear, setVisibleYear] = useState<number>(initialMonth.getFullYear());
  const [visibleMonth, setVisibleMonth] = useState<number>(initialMonth.getMonth());

  const monthLabel = useMemo(() => {
    return new Date(visibleYear, visibleMonth, 1).toLocaleString(locale, { month: "long" });
  }, [locale, visibleMonth, visibleYear]);

  const years = useMemo(() => {
    const base = parsedValue?.getFullYear() ?? new Date().getFullYear();
    const start = base - 7;
    const end = base + 3;
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [parsedValue]);

  const weekdays = useMemo(() => {
    const base = weekStartsOnSunday
      ? ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
      : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    // Jan 2024: day 1 = Mon, day 7 = Sun — used as a stable reference to format each weekday name.
    const dayOfMonthByKey: Record<string, number> = {
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
      Sun: 7,
    };
    return base.map((key) =>
      new Date(2024, 0, dayOfMonthByKey[key]).toLocaleDateString(locale, { weekday: "short" })
    );
  }, [locale, weekStartsOnSunday]);

  const grid = useMemo(() => {
    const firstOfMonth = new Date(visibleYear, visibleMonth, 1);
    const daysInMonth = getDaysInMonth(visibleYear, visibleMonth);
    const leading = getWeekdayIndex(firstOfMonth, weekStartsOnSunday);

    const cells: Array<{ day: number | null; iso?: string }> = [];
    for (let i = 0; i < leading; i++) cells.push({ day: null });
    for (let day = 1; day <= daysInMonth; day++) {
      const iso = toISODate(new Date(visibleYear, visibleMonth, day));
      cells.push({ day, iso });
    }
    while (cells.length % 7 !== 0) cells.push({ day: null });
    return cells;
  }, [visibleMonth, visibleYear, weekStartsOnSunday]);

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
    if (parsedValue) {
      setVisibleYear(parsedValue.getFullYear());
      setVisibleMonth(parsedValue.getMonth());
    }
  };

  const handleClose = () => setAnchorEl(null);

  const monthKey = (year: number, monthIndex: number) => `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
  const prevMonth = clampMonth(visibleYear, visibleMonth - 1);
  const nextMonth = clampMonth(visibleYear, visibleMonth + 1);
  // Mês inteiro fora da faixa: a navegação para ele é bloqueada.
  const prevDisabled = !!minDate && monthKey(prevMonth.year, prevMonth.monthIndex) < minDate.slice(0, 7);
  const nextDisabled = !!maxDate && monthKey(nextMonth.year, nextMonth.monthIndex) > maxDate.slice(0, 7);
  const today = toISODate(new Date());
  const todayOutOfRange = !onToday && clampIso(today, minDate, maxDate) !== today;

  const handlePrev = () => {
    const next = clampMonth(visibleYear, visibleMonth - 1);
    setVisibleYear(next.year);
    setVisibleMonth(next.monthIndex);
  };

  const handleNext = () => {
    const next = clampMonth(visibleYear, visibleMonth + 1);
    setVisibleYear(next.year);
    setVisibleMonth(next.monthIndex);
  };

  return (
    <>
      <Input
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={handleOpen}
        size={size}
        disabled={disabled}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        inputProps={{ readOnly: false, inputMode: "none" }}
        sx={{ minWidth, marginY: 0, ...sx }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label={label} edge="end" onClick={handleOpen} disabled={disabled}>
                <CalendarMonthOutlined fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 320 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <IconButton onClick={handlePrev} disabled={prevDisabled} aria-label="Previous month">
              <ChevronLeft />
            </IconButton>

            <Stack direction="row" spacing={1} alignItems="center">
              <Select
                sx={{ minWidth: 140, marginY: 0 }}
                value={visibleMonth}
                label={intl.formatMessage({ id: "datePicker.month" })}
                onChange={(e) => setVisibleMonth(Number(e.target.value))}
                MenuProps={menuProps}
                renderValue={() => monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)}
              >
                {Array.from({ length: 12 }, (_, month) => (
                  <MenuItem key={month} value={month}>
                    {new Date(visibleYear, month, 1).toLocaleString(locale, { month: "long" })}
                  </MenuItem>
                ))}
              </Select>

              <Select
                sx={{ minWidth: 90, marginY: 0 }}
                value={visibleYear}
                label={intl.formatMessage({ id: "datePicker.year" })}
                onChange={(e) => setVisibleYear(Number(e.target.value))}
                MenuProps={menuProps}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </Stack>

            <IconButton onClick={handleNext} disabled={nextDisabled} aria-label="Next month">
              <ChevronRight />
            </IconButton>
          </Stack>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, mb: 1 }}>
            {weekdays.map((wd, idx) => (
              <Typography
                key={idx}
                variant="caption"
                sx={{ textAlign: "center", color: "text.secondary", fontWeight: fontWeights.bold }}
              >
                {wd}
              </Typography>
            ))}
            {grid.map((cell, idx) => {
              const isSelected = !!cell.iso && cell.iso === value;
              const outOfRange =
                !!cell.iso && ((!!minDate && cell.iso < minDate) || (!!maxDate && cell.iso > maxDate));
              return (
                <LoadingButton
                  key={`${idx}-${cell.day ?? "x"}`}
                  size="small"
                  variant={isSelected ? "contained" : "text"}
                  disabled={!cell.iso || outOfRange}
                  onClick={() => {
                    if (cell.iso) onChange(cell.iso);
                    handleClose();
                  }}
                  sx={{
                    minWidth: 0,
                    px: 0,
                    py: 0.5,
                    lineHeight: 1.2,
                    borderRadius: 1,
                  }}
                >
                  {cell.day ?? ""}
                </LoadingButton>
              );
            })}
          </Box>

          <Stack direction="row" spacing={1} justifyContent="space-between">
            <LoadingButton
              size="small"
              onClick={() => {
                if (onClear) onClear();
                else onChange("");
                handleClose();
              }}
            >
              {intl.formatMessage({ id: "datePicker.clear" })}
            </LoadingButton>
            <LoadingButton
              size="small"
              variant="contained"
              disabled={todayOutOfRange}
              onClick={() => {
                if (onToday) onToday();
                else onChange(today);
                handleClose();
              }}
            >
              {intl.formatMessage({ id: "datePicker.today" })}
            </LoadingButton>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
